/**
 * Email Messages Service for Outlook Add-in
 * Handles creating email message records in DoNexus backend
 */

import apiClient from '../utils/apiClient';

const normalizeOfficeId = (id) => {
  if (typeof id !== 'string') {
    return id;
  }
  return id.replace(/\+/g, '_').replace(/\//g, '-');
};

class EmailMessagesService {
  /**
   * Create an email message from Outlook
   * @param {Object} connection - Connection object with id and email_address
   * @param {Object} emailContent - Email content from Outlook
   * @returns {Promise<Object>} Created email message
   */
  async createOutlookEmailMessage(connection, emailContent) {
    try {
      console.log('📧 Creating Outlook email message', { 
        connectionId: connection?.id,
        emailId: emailContent?.id 
      });

      const normalizedEmailContent = {
        ...emailContent,
        id: normalizeOfficeId(emailContent?.id),
        conversationId: normalizeOfficeId(emailContent?.conversationId)
      };

      const response = await apiClient.post('/email-messages/outlook', {
        connection,
        emailContent: normalizedEmailContent
      });

      if (response.success && response.data) {
        console.log('✅ Email message created successfully:', response.data);
        return {
          success: true,
          data: response.data
        };
      } else {
        throw new Error(response.error || response.message || 'Failed to create email message');
      }
    } catch (error) {
      console.error('❌ Error creating Outlook email message:', error);
      throw error;
    }
  }

  /**
   * Get email content from current Office.js item
   * @returns {Promise<Object>} Email content object formatted for backend
   */
  async getCurrentEmailContent() {
    try {
      if (typeof Office === 'undefined' || !Office.context || !Office.context.mailbox || !Office.context.mailbox.item) {
        throw new Error('Office.js context not available');
      }

      const item = Office.context.mailbox.item;
      
      // Determine if we're in Read mode or Compose mode
      // In Read mode: properties are synchronous
      // In Compose mode: properties need async getAsync() calls
      const isReadMode = item.itemType === Office.MailboxEnums.ItemType.Message && 
                         typeof item.subject === 'string';
      
      console.log(`📧 Email mode detected: ${isReadMode ? 'Read' : 'Compose'}`);
      
      // Get basic properties synchronously
      const emailContent = {
        id: item.itemId,
        conversationId: item.conversationId,
        internetMessageId: item.internetMessageId,
        subject: null,
        from: null,
        sender: null,
        toRecipients: [],
        ccRecipients: [],
        bccRecipients: [],
        body: null,
        bodyPreview: null,
        receivedDateTime: null,
        hasAttachments: item.hasAttachments || false,
        importance: item.importance || 'normal'
      };

      if (isReadMode) {
        // Read mode - properties are synchronous
        emailContent.subject = item.subject || '';
        
        // Get from
        if (item.from) {
          emailContent.from = {
            emailAddress: {
              name: item.from.displayName,
              address: item.from.emailAddress
            }
          };
        }
        
        // Get sender (fallback)
        if (item.sender) {
          emailContent.sender = {
            emailAddress: {
              name: item.sender.displayName,
              address: item.sender.emailAddress
            }
          };
        }
        
        // Get To recipients
        if (item.to && Array.isArray(item.to)) {
          emailContent.toRecipients = item.to.map(r => ({
            emailAddress: {
              name: r.displayName,
              address: r.emailAddress
            }
          }));
        }
        
        // Get CC recipients
        if (item.cc && Array.isArray(item.cc)) {
          emailContent.ccRecipients = item.cc.map(r => ({
            emailAddress: {
              name: r.displayName,
              address: r.emailAddress
            }
          }));
        }
        
        // Get BCC recipients (if available)
        if (item.bcc && Array.isArray(item.bcc)) {
          emailContent.bccRecipients = item.bcc.map(r => ({
            emailAddress: {
              name: r.displayName,
              address: r.emailAddress
            }
          }));
        }
        
        // Get received date time
        if (item.dateTimeCreated) {
          emailContent.receivedDateTime = item.dateTimeCreated.toISOString();
        }
        
        // Get body content asynchronously (body is always async)
        await Promise.all([
          // Get body as HTML
          new Promise((resolve) => {
            item.body.getAsync(Office.CoercionType.Html, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                emailContent.body = {
                  contentType: 'html',
                  content: result.value || ''
                };
              }
              resolve();
            });
          }),

          // Get body preview (first 255 chars as text)
          new Promise((resolve) => {
            item.body.getAsync(Office.CoercionType.Text, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const text = result.value || '';
                emailContent.bodyPreview = text.substring(0, 255);
              }
              resolve();
            });
          })
        ]);
        
      } else {
        // Compose mode - use async getAsync() calls
        await Promise.all([
          // Get subject
          new Promise((resolve) => {
            item.subject.getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                emailContent.subject = result.value || '';
              }
              resolve();
            });
          }),
          
          // Get from/sender
          new Promise((resolve) => {
            if (item.from && typeof item.from.getAsync === 'function') {
              item.from.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  emailContent.from = {
                    emailAddress: {
                      name: result.value.displayName,
                      address: result.value.emailAddress
                    }
                  };
                }
                resolve();
              });
            } else {
              resolve();
            }
          }),

          // Get To recipients
          new Promise((resolve) => {
            item.to.getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                emailContent.toRecipients = result.value.map(r => ({
                  emailAddress: {
                    name: r.displayName,
                    address: r.emailAddress
                  }
                }));
              }
              resolve();
            });
          }),

          // Get CC recipients
          new Promise((resolve) => {
            if (item.cc && typeof item.cc.getAsync === 'function') {
              item.cc.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  emailContent.ccRecipients = result.value.map(r => ({
                    emailAddress: {
                      name: r.displayName,
                      address: r.emailAddress
                    }
                  }));
                }
                resolve();
              });
            } else {
              resolve();
            }
          }),

          // Get BCC recipients (if available)
          new Promise((resolve) => {
            if (item.bcc && typeof item.bcc.getAsync === 'function') {
              item.bcc.getAsync((result) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  emailContent.bccRecipients = result.value.map(r => ({
                    emailAddress: {
                      name: r.displayName,
                      address: r.emailAddress
                    }
                  }));
                }
                resolve();
              });
            } else {
              resolve();
            }
          }),

          // Get body as HTML
          new Promise((resolve) => {
            item.body.getAsync(Office.CoercionType.Html, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                emailContent.body = {
                  contentType: 'html',
                  content: result.value || ''
                };
              }
              resolve();
            });
          }),

          // Get body preview (first 255 chars as text)
          new Promise((resolve) => {
            item.body.getAsync(Office.CoercionType.Text, (result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const text = result.value || '';
                emailContent.bodyPreview = text.substring(0, 255);
              }
              resolve();
            });
          }),

          // Get received date time
          new Promise((resolve) => {
            if (item.dateTimeCreated) {
              emailContent.receivedDateTime = item.dateTimeCreated.toISOString();
            }
            resolve();
          })
        ]);

        // If from is not available, try to get sender as fallback
        if (!emailContent.from && item.sender && typeof item.sender.getAsync === 'function') {
          await new Promise((resolve) => {
            item.sender.getAsync((result) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                emailContent.sender = {
                  emailAddress: {
                    name: result.value.displayName,
                    address: result.value.emailAddress
                  }
                };
              }
              resolve();
            });
          });
        }
      }

      console.log('✅ Email content extracted:', {
        id: emailContent.id,
        subject: emailContent.subject,
        hasBody: !!emailContent.body?.content
      });

      return emailContent;
    } catch (error) {
      console.error('❌ Error getting email content:', error);
      throw error;
    }
  }
}

// Create singleton instance
const emailMessagesService = new EmailMessagesService();

export default emailMessagesService;

